// Adds the shared API key to every request that goes to the Scheduler backend, in one place.
//
// The Scheduler backend now requires the key on everything except the OAuth login pages. There are
// about twenty call sites; patching each one would leave the next one someone writes unprotected, and
// a missing key would show up as an empty screen. So this wraps window.fetch once, at startup:
//   - only requests whose address starts with the Scheduler backend's address get the key
//     (the key must never be sent to any other host: Media Studio has its own calls, Claude, etc.)
//   - a request that already carries its own x-api-key is left alone
//   - with no key built into this build nothing is added (the missing-key banner already says so)
import { SCHEDULER_BACKEND, MEDIA_STUDIO_API_KEY } from './constants';

const sameOrigin = (url, base) => {
  try { const a = new URL(url, base); const b = new URL(base); return a.origin === b.origin && a.pathname.startsWith(b.pathname === '/' ? '/' : b.pathname); }
  catch { return false; }
};

export function withSchedulerKey(fetchImpl, { base = SCHEDULER_BACKEND, key = MEDIA_STUDIO_API_KEY } = {}) {
  return function schedulerFetch(input, init) {
    if (!key) return fetchImpl(input, init);
    const url = typeof input === 'string' ? input : (input && input.url) || String(input);
    // only absolute addresses on the Scheduler backend's own origin; a relative URL is this website's own
    if (!/^https?:\/\//i.test(url) || !sameOrigin(url, base)) return fetchImpl(input, init);
    const headers = new Headers((init && init.headers) || (typeof input !== 'string' && input.headers) || undefined);
    if (!headers.has('x-api-key')) headers.set('x-api-key', key);
    return fetchImpl(input, { ...(init || {}), headers });
  };
}

export function installSchedulerAuth(target = window) {
  if (target.__schedulerAuthInstalled) return;
  target.fetch = withSchedulerKey(target.fetch.bind(target));
  target.__schedulerAuthInstalled = true;
}
